import type { CSSProperties } from 'react';
import type { GameId } from '@tg/shared';
import { accentUri, hasScene, SCENE_GAMES, sceneUri } from '@/art/scenes';
import { rasterBg } from '@/art/raster';
import type { RasterBgKeys } from '@/art/raster.gen';
import type { ArtTheme } from '@/art/covers/kit';
import { useResolvedTheme } from '@/theme/theme';

/*
 * "Material" of a game screen (owner decision 2026-09-30: roll it out to every game): the game zone gets a quiet scene,
 * the board frame and the bet / control panel get the game's surface (gradient, 1px edge, soft shadow, radius, accent) —
 * navigation, forms, tables, header and footer stay neutral.
 *
 * A game has a material when it has a scene file `art/scenes/<game>.ts` (picked up by a glob, no shared list) and its
 * page passes `game` to GameShell; its tokens are `styles/material/<game>.css` (loaded below, also by a glob).
 * Step by step: art/scenes/README.md.
 */
import.meta.glob('../styles/material/*.css', { eager: true });

/** The games with a material (= a scene in art/scenes). */
export const MATERIAL_GAMES: ReadonlySet<GameId> = new Set<GameId>(SCENE_GAMES);

/** The material of a game, or null when the screen keeps the plain look. */
export function materialOf(game: GameId | undefined | null): GameId | null {
  return game && hasScene(game) ? game : null;
}

export interface MaterialAttrs {
  'data-material'?: 'on';
  'data-game'?: string;
  style?: CSSProperties;
}

/**
 * Raster scenes that do not fit their theme and stay off until Codex redraws them (`<game>:<theme>`): the vector scene
 * shows instead. Empty now — firecrackers/scene-light was redrawn as a daylight picture (Codex, 2026-10-01).
 */
const RASTER_SCENE_OFF: ReadonlySet<string> = new Set<string>();

/** Raster scene of a non-slot game from art:process (`scene-dark|light`), or null — slots set their own in SlotGame. */
function rasterScene(game: GameId, theme: ArtTheme): string | null {
  if (RASTER_SCENE_OFF.has(`${game}:${theme}`)) return null;
  const g = game as keyof RasterBgKeys;
  return rasterBg(g, 'scene' as RasterBgKeys[typeof g], theme);
}

/** Attributes for the game screen container: the flag, the game and the scene / accent images. */
export function materialAttrs(game: GameId | undefined | null, theme: ArtTheme): MaterialAttrs {
  const m = materialOf(game);
  if (!m) return {};
  const url = (u: string) => `url("${u}")`;
  const raster = rasterScene(m, theme);
  return {
    'data-material': 'on',
    'data-game': m,
    style: {
      '--mat-scene': url(sceneUri(m, theme, 'wide')),
      '--mat-scene-m': url(sceneUri(m, theme, 'compact')),
      '--mat-accent-strip': url(accentUri(m, theme)),
      // the raster scene lies over the vector one in each game's CSS (`var(--scene-bg, none)`)
      ...(raster ? { '--scene-bg': url(raster) } : {}),
    } as CSSProperties,
  };
}

/** `attrs` go on the game screen container (GameShell frame); `on` = the material is enabled for this game. */
export function useGameMaterial(game: GameId | undefined | null): { on: boolean; attrs: MaterialAttrs } {
  const theme = useResolvedTheme();
  const attrs = materialAttrs(game, theme);
  return { on: !!attrs['data-material'], attrs };
}
